import { useEffect, useRef, useState } from 'react';
import { Eraser } from 'lucide-react';
import { Button } from '@/design/components';

/**
 * חתימה ביד, בעכבר או באצבע.
 *
 * Pointer Events ולא mouse ו touch בנפרד: אותו קוד עובד בעכבר,
 * באצבע ובעט, ואין כפילות שמתפצלת.
 *
 * touch-none על הקנבס הכרחי: בלעדיו גרירה באצבע גוללת את הדף
 * במקום לצייר, והמסך נראה שבור בטלפון דווקא — המקום שבו חותמים.
 *
 * הקנבס נדגם לפי devicePixelRatio, אחרת החתימה יוצאת מטושטשת
 * בדיוק במסמך שאמור להיות ראיה.
 */
export function SignaturePad({
  onChange,
}: {
  /** null = נוקה. אחרת blob של PNG שקוף. */
  onChange: (signature: Blob | null) => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  const [hasInk, setHasInk] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ratio = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = Math.round(rect.width * ratio);
    canvas.height = Math.round(rect.height * ratio);

    const context = canvas.getContext('2d');
    if (!context) return;
    context.scale(ratio, ratio);
    context.lineWidth = 2;
    context.lineCap = 'round';
    context.lineJoin = 'round';
    context.strokeStyle = '#1b1b1b';
  }, []);

  const pointAt = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  };

  const emit = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.toBlob((blob) => onChange(blob), 'image/png');
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext('2d');
    if (!canvas || !context) return;
    context.clearRect(0, 0, canvas.width, canvas.height);
    setHasInk(false);
    onChange(null);
  };

  return (
    <div className="grid gap-2">
      <canvas
        ref={canvasRef}
        aria-label="אזור החתימה"
        className="h-40 w-full touch-none rounded-sm border border-dashed border-muted bg-surface"
        onPointerDown={(event) => {
          event.currentTarget.setPointerCapture(event.pointerId);
          const context = event.currentTarget.getContext('2d');
          if (!context) return;
          const point = pointAt(event);
          context.beginPath();
          context.moveTo(point.x, point.y);
          drawing.current = true;
          setHasInk(true);
        }}
        onPointerMove={(event) => {
          if (!drawing.current) return;
          const context = event.currentTarget.getContext('2d');
          if (!context) return;
          const point = pointAt(event);
          context.lineTo(point.x, point.y);
          context.stroke();
        }}
        onPointerUp={() => {
          if (!drawing.current) return;
          drawing.current = false;
          emit();
        }}
        onPointerLeave={() => {
          if (!drawing.current) return;
          drawing.current = false;
          emit();
        }}
      />

      <div className="flex flex-wrap items-center gap-2">
        <Button variant="secondary" disabled={!hasInk} onClick={clear}>
          <Eraser size={18} strokeWidth={1.75} aria-hidden="true" />
          ניקוי החתימה
        </Button>
        <span className="text-body-sm text-muted">אפשר לחתום בעכבר, באצבע או בעט.</span>
      </div>
    </div>
  );
}
